// Python interview questions data
export const pythonQuestions = {
  'basics': [
    {
      id: 1,
      question: "What is Python?",
      answer: "Python is a popular programming language that reads almost like English. It is high-level (you do not manage memory by hand), interpreted (you run source directly with a runtime), and dynamically typed (a variable's type can change as the program runs). You can write object-oriented, functional, or simple script-style code in it."
    },
    {
      id: 2,
      question: "Is Python compiled or interpreted?",
      answer: "Both ideas apply. Your .py file is first turned into bytecode (.pyc), then the Python Virtual Machine (PVM) runs that bytecode. That is how CPython works. PyPy is another implementation that can use a JIT to go faster."
    },
    {
      id: 3,
      question: "What is a dynamically typed language?",
      answer: "Types are figured out while the program runs, not written in stone at compile time. If you write x = 10 then x is an int; if the next line sets x = 'hello', x is now a str. You do not declare types the way you do in Java or C."
    },
    {
      id: 4,
      question: "What are Python's key features?",
      answer: "It is interpreted, dynamically typed, and garbage-collected (unused objects get cleaned up). It supports OOP and functional style, ships with a large standard library, has simple syntax, and runs on many platforms. That mix made it huge for web backends, data work, AI, and small scripts."
    },
    {
      id: 5,
      question: "What is PEP 8?",
      answer: "PEP 8 is the official style guide for how to format Python so teams read each other’s code easily. It covers indentation (4 spaces), line length, naming (snake_case for functions, etc.), and spacing. Open-source projects usually expect PEP 8 on contributions."
    },
    {
      id: 6,
      question: "What is the difference between / and // in Python?",
      answer: "/ is true division and returns a float (5/2 is 2.5). // is floor division: it drops the fractional part toward negative infinity (5//2 is 2)."
    },
    {
      id: 7,
      question: "What is indentation in Python and why is it important?",
      answer: "Python uses leading spaces to show what belongs inside a function, loop, or if block, instead of curly braces. Wrong indentation is an IndentationError, not just ugly style, so it is part of the language rules."
    },
    {
      id: 8,
      question: "What is the pass statement?",
      answer: "pass is a placeholder that does nothing but satisfies Python’s need for a statement. Use it in an empty function, class, or branch you have not filled in yet. Unlike a comment, pass is real code the interpreter runs."
    }
  ],

  'data-types': [
    {
      id: 1,
      question: "What are the built-in data types in Python?",
      answer: "Numbers: int, float, complex. Sequences: str, list, tuple, range. Mapping: dict. Sets: set and frozenset. Boolean: bool. Binary: bytes. The type comes from the value you assign, not from a separate declaration."
    },
    {
      id: 2,
      question: "Difference between list, tuple, and set.",
      answer: "list: ordered, changeable, duplicates allowed, written with []. tuple: ordered, fixed after creation, duplicates allowed, written with (). set: unordered collection of unique items, written with {}. Use tuples for fixed records, sets for fast membership tests, lists when you need to grow or reorder often."
    },
    {
      id: 3,
      question: "Difference between mutable and immutable types.",
      answer: "Mutable values can be changed in place (list, dict, set). Immutable values cannot be changed without making a new object (int, str, tuple, frozenset). When you \"change\" a string, Python actually builds a new string behind the scenes."
    },
    {
      id: 4,
      question: "Difference between list and tuple.",
      answer: "Lists can be modified and are a bit heavier. Tuples are fixed, a bit faster and smaller, and can be used as dict keys if their contents are hashable. Pick tuples for data that should not change shape; lists for working collections."
    },
    {
      id: 5,
      question: "What is a dictionary in Python?",
      answer: "A dict maps keys to values. From 3.7 onward insertion order is preserved. Keys must be unique and immutable (strings, numbers, tuples of immutables). Lookup is fast (hash table), O(1) average. Example: {'name': 'Deepam', 'age': 21}."
    },
    {
      id: 6,
      question: "Difference between a set and a dictionary.",
      answer: "A set is only values: {1, 2, 3}. A dict is key to value: {'a': 1}. Both can use curly braces, but {} by itself creates an empty dict. For an empty set you must call set()."
    },
    {
      id: 7,
      question: "What is a frozenset?",
      answer: "frozenset is like set but frozen: you can union and intersect, but you cannot add or remove items after creation. Because it never changes, it can be a dict key, unlike a normal set."
    },
    {
      id: 8,
      question: "How is a list different from an array?",
      answer: "Built-in lists can hold mixed types and are very flexible. The array module and NumPy arrays store one type in a tight layout, which saves memory and speeds up numeric work. For heavy math, prefer NumPy arrays."
    }
  ],

  'functions': [
    {
      id: 1,
      question: "What is a lambda function?",
      answer: "A lambda is a tiny anonymous function in one expression: lambda x: x**2. Use it for short callbacks with map(), filter(), or sorted(), not for big logic (a normal def is clearer)."
    },
    {
      id: 2,
      question: "What are *args and **kwargs?",
      answer: "*args collects extra positional arguments into a tuple. **kwargs collects extra keyword arguments into a dict. Together you can write def f(*args, **kwargs) and accept flexible call patterns."
    },
    {
      id: 3,
      question: "What is a decorator?",
      answer: "A decorator is a function that wraps another function to add behavior (logging, timing, auth) without editing the inner function’s body. You apply it with @name above the def. It is just syntactic sugar for passing a function into a wrapper."
    },
    {
      id: 4,
      question: "What is a generator?",
      answer: "A generator function uses yield instead of return. Each time you ask for the next value, it runs until the next yield and then pauses. That is lazy: you do not build the whole list in memory, which helps large or endless sequences."
    },
    {
      id: 5,
      question: "Difference between a generator and a normal function.",
      answer: "A normal function runs to the end and returns once. A generator yields many values over time and remembers where it stopped. Generators are better when the sequence is large or infinite."
    },
    {
      id: 6,
      question: "What is a closure?",
      answer: "A closure is an inner function that still sees variables from the outer function after the outer call finished. That \"memory\" of the outer scope is what powers many decorators and factory functions."
    },
    {
      id: 7,
      question: "What is the difference between map(), filter(), and reduce()?",
      answer: "map(func, iterable) runs func on every item and gives you those results. filter(func, iterable) keeps items where func returns True. reduce(func, iterable), from functools, combines items step by step into one value (like multiplying a whole list)."
    },
    {
      id: 8,
      question: "How are arguments passed in Python: by value or by reference?",
      answer: "People say \"pass by object reference.\" The function gets a reference to the same object. If the object is immutable (int, str), changes inside the function do not replace the caller’s variable (it looks like pass-by-value). If it is mutable (list, dict), in-place edits are visible to the caller."
    }
  ],

  'oop': [
    {
      id: 1,
      question: "What are the four pillars of OOP in Python?",
      answer: "Encapsulation: bundle data and methods and hide details. Inheritance: child classes reuse parent code. Polymorphism: the same method name can do different things per class. Abstraction: expose only what callers need, hide internals."
    },
    {
      id: 2,
      question: "What is the difference between a class and an object?",
      answer: "A class is the blueprint (fields and methods). An object is one concrete instance built from that class, with real values. Car might be the class; my_car = Car('Tesla') is an object."
    },
    {
      id: 3,
      question: "What is self in Python?",
      answer: "self is the first parameter of normal instance methods and points at the current instance. It is not a reserved word, only a strong convention. Python passes the instance for you when you call obj.method()."
    },
    {
      id: 4,
      question: "What is __init__?",
      answer: "__init__ is the initializer: Python calls it right after your object is created so you can set attributes. Example: def __init__(self, name): self.name = name."
    },
    {
      id: 5,
      question: "What are dunder (magic) methods?",
      answer: "Dunder means double underscore, like __init__, __str__, __len__, __add__. Python calls them for built-in behavior: printing, len(), +, and so on. You customize how your class plugs into those operations."
    },
    {
      id: 6,
      question: "What are the types of inheritance in Python?",
      answer: "Single (one parent), multiple (several parents), multilevel (A extends B extends C), hierarchical (many children of one parent), and hybrid mixes. Python resolves method order with the MRO (Method Resolution Order), especially when parents overlap."
    },
    {
      id: 7,
      question: "What is MRO (Method Resolution Order)?",
      answer: "MRO is the order Python walks the base classes to find a method. With multiple inheritance it uses the C3 linearization rules. You can inspect it with ClassName.__mro__ or ClassName.mro()."
    },
    {
      id: 8,
      question: "Difference between @classmethod and @staticmethod.",
      answer: "@classmethod gets the class as first argument (cls) and can read or change class-level data; you can call it on the class without an instance. @staticmethod is a plain function sitting on the class for grouping: no cls, no self, no automatic access to instance or class state."
    },
    {
      id: 9,
      question: "What is encapsulation in Python?",
      answer: "Encapsulation means hiding how data works inside the object and exposing a small public surface. Python uses naming: public name, _protected by convention, __private triggers name mangling to _ClassName__name. Nothing is truly private, but conventions guide safe use."
    },
    {
      id: 10,
      question: "What is polymorphism?",
      answer: "Same interface, different types underneath. Subclasses override parent methods, or duck typing applies: if it has the methods you need, you use it without a formal interface keyword."
    }
  ],

  'memory-and-scope': [
    {
      id: 1,
      question: "What is the LEGB rule in Python?",
      answer: "When Python looks up a name it checks Local (inside the function), then Enclosing (outer functions in nested defs), then Global (module level), then Built-in (len, print, etc.). That order is LEGB."
    },
    {
      id: 2,
      question: "What is the difference between global and local variables?",
      answer: "Locals live inside a function and vanish when the call ends (unless captured in a closure). Globals live at module scope and are visible everywhere unless shadowed. Use the global keyword inside a function if you truly need to rebind a global name."
    },
    {
      id: 3,
      question: "What is garbage collection in Python?",
      answer: "Main idea is reference counting: when nothing points to an object anymore, it can go away immediately. A separate cyclic GC fixes reference rings the counter alone cannot see. Together they reclaim memory you are not using."
    },
    {
      id: 4,
      question: "What is the difference between deep copy and shallow copy?",
      answer: "Shallow copy (copy.copy) makes a new outer object but inner lists or dicts are still shared. Deep copy (copy.deepcopy) clones nested objects too, so changes deep inside do not leak back to the original."
    },
    {
      id: 5,
      question: "What is the GIL (Global Interpreter Lock)?",
      answer: "In CPython, the GIL is a lock so only one thread runs Python bytecode at a time. That makes memory handling simpler but limits CPU-bound parallelism across threads on one process. For heavy CPU work people use multiprocessing or native extensions."
    },
    {
      id: 6,
      question: "What is the difference between is and ==?",
      answer: "== compares values (are these equal?). is compares identity (are these the exact same object in memory?). Two different lists with the same numbers can be == but not is."
    }
  ],

  'comprehensions-and-iterators': [
    {
      id: 1,
      question: "What is list comprehension?",
      answer: "A compact way to build a list: [expr for item in iterable if condition]. Example: [x**2 for x in range(10) if x % 2 == 0] builds squares of even numbers. Often clearer and a bit faster than a manual for-append loop."
    },
    {
      id: 2,
      question: "What is dictionary comprehension?",
      answer: "Same idea for dicts: {k: v for ...}. Example: {x: x**2 for x in range(5)}. One expression instead of creating an empty dict and looping."
    },
    {
      id: 3,
      question: "What is the difference between an iterator and an iterable?",
      answer: "An iterable has __iter__ and can be looped (list, str). Calling iter() on it gives an iterator. The iterator has __next__ and returns one item at a time until StopIteration."
    },
    {
      id: 4,
      question: "What is a generator expression?",
      answer: "Like a list comprehension but with parentheses: (x**2 for x in range(10)). It yields values lazily instead of building a full list, so memory stays small."
    },
    {
      id: 5,
      question: "What is the difference between range() and xrange()?",
      answer: "Python 3 only has range(). It behaves like Python 2’s xrange: it does not build the whole list in RAM; it produces numbers on demand. In Python 2, range() made a full list, which was heavy for big spans."
    }
  ],

  'exception-handling': [
    {
      id: 1,
      question: "What is exception handling in Python?",
      answer: "You wrap risky code in try, catch problems with except, optionally run else when no error happened, and use finally for cleanup that must always run (close files, release locks)."
    },
    {
      id: 2,
      question: "What is the difference between Exception and Error?",
      answer: "Both inherit from BaseException. Many docs call unrecoverable issues like SyntaxError or MemoryError \"errors\" and everyday fixable ones like ValueError or KeyError \"exceptions,\" but the language treats them the same family: you catch what you can handle and usually let the rest crash loudly."
    },
    {
      id: 3,
      question: "What is the finally block used for?",
      answer: "finally runs whether try succeeded, failed, or returned inside try. Put cleanup there: close files, disconnect databases, release locks."
    },
    {
      id: 4,
      question: "How do you raise a custom exception in Python?",
      answer: "Subclass Exception (or a closer built-in), then raise YourError('message'). That gives clear, domain-specific errors your callers can catch on purpose."
    },
    {
      id: 5,
      question: "What is the difference between raise and raise e?",
      answer: "A bare raise inside except rethrows the current exception and keeps the original traceback. raise e wraps or rebinds and can reset the traceback to the current line, which hides where the bug really started. Prefer bare raise when rethrowing."
    }
  ],

  'advanced': [
    {
      id: 1,
      question: "What are Python modules and packages?",
      answer: "A module is one .py file you import. A package is a folder of modules with __init__.py (or namespace package rules) so you can write import pkg.submod."
    },
    {
      id: 2,
      question: "What is __name__ == '__main__'?",
      answer: "When you run a file directly, __name__ is set to '__main__'. When another file imports it, __name__ is the module name. The if __name__ == '__main__': block runs demo or CLI code only on direct execution, not on import."
    },
    {
      id: 3,
      question: "What is a context manager and the with statement?",
      answer: "A context manager defines __enter__ and __exit__ so setup and teardown always pair. with open('f.txt') as f: guarantees the file closes even if an error happens inside the block."
    },
    {
      id: 4,
      question: "What is monkey patching?",
      answer: "Changing or replacing attributes on a class or module at runtime, often in tests to swap a function with a mock. Powerful for tests, risky in production because behavior becomes harder to trace."
    },
    {
      id: 5,
      question: "What are Python's access modifiers?",
      answer: "Convention only: no access modifier keywords. Single leading underscore means \"please treat as internal.\" Double leading underscore triggers name mangling on attributes to reduce accidental overrides in subclasses. Nothing stops a determined caller."
    },
    {
      id: 6,
      question: "What is the difference between multiprocessing and multithreading in Python?",
      answer: "Threads share one process and one GIL in CPython, so CPU-bound Python threads do not run in parallel on multiple cores. Processes each have their own Python and memory, so they can use multiple cores. Use threads for waiting on I/O; use multiprocessing for heavy CPU Python work."
    },
    {
      id: 7,
      question: "What is pickling and unpickling?",
      answer: "The pickle module turns Python objects into bytes (pickling) and back (unpickling). Common for saving models, caches, or sending objects, but only unpickle data you trust because it can run arbitrary code on load in some setups."
    }
  ]
};

// Helper function to get all questions from all Python topics
export const getAllPythonQuestions = () => {
  const allQuestions = [];
  Object.keys(pythonQuestions).forEach(topic => {
    pythonQuestions[topic].forEach(q => {
      allQuestions.push({ ...q, topic });
    });
  });
  return allQuestions;
};

// Helper function to shuffle array
export const shuffleArray = (array) => {
  const shuffled = [...array];
  for (let i = shuffled.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
  }
  return shuffled;
};
